import { useCallback, useEffect, useMemo, useState } from "react";
import { api, API, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Clock, Download, FilePlus2, Printer, Save, Shield, UserPlus } from "lucide-react";

const EMPTY_WORKER = { name: "", surname: "", role: "camarero", pin: "", active: true };
const EMPTY_MANUAL = { worker_id: "", type: "clock_in", timestamp_local: "", manual_reason: "Olvido" };
const EMPTY_CORRECTION = { entry_id: "", corrected_timestamp_local: "", reason: "" };
const EMPTY_VOID = { entry_id: "", reason: "" };
const TEST_DELETE_PHRASE = "ELIMINAR PRUEBAS";
const ROLES = [
  ["camarero", "Camarero"],
  ["jefe_sala", "Jefe de sala"],
  ["cocina", "Cocina"],
  ["auxiliar_cocina", "Auxiliar cocina"],
  ["auxiliar_mesas", "Auxiliar mesas"],
  ["otro", "Otro"],
];
const ROLE_LABELS = {
  camarero: "Camarero",
  jefe_sala: "Jefe de sala",
  cocina: "Cocina",
  auxiliar: "Auxiliar cocina",
  perrillero: "Auxiliar mesas",
  auxiliar_cocina: "Auxiliar cocina",
  auxiliar_mesas: "Auxiliar mesas",
  otro: "Otro",
};
const FIELD_CLASS = "w-full rounded-lg border border-border px-3 py-2 text-sm";

function todayISO() {
  return new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Madrid" });
}

function toLocalInput(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const parts = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Madrid",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(date).reduce((acc, part) => ({ ...acc, [part.type]: part.value }), {});
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

function formatDateTime(value) {
  if (!value) return "";
  return new Date(value).toLocaleString("es-ES", { timeZone: "Europe/Madrid" });
}

function formatDate(value) {
  if (!value) return "";
  const [year, month, day] = value.split("-");
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
}

function formatTime(value) {
  if (!value) return "";
  return new Date(value).toLocaleTimeString("es-ES", { timeZone: "Europe/Madrid", hour: "2-digit", minute: "2-digit" });
}

function formatMinutes(minutes) {
  const safe = Number(minutes) || 0;
  const h = Math.floor(safe / 60);
  const m = safe % 60;
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

function effectiveTimestamp(entry) {
  return entry?.correction?.corrected_timestamp_utc || entry?.timestamp_utc;
}

function isTestEntry(entry) {
  return entry?.is_test === true;
}

function computePairs(entries) {
  const sorted = [...entries].filter((entry) => !entry.voided).sort((a, b) => new Date(effectiveTimestamp(a)) - new Date(effectiveTimestamp(b)));
  let open = null;
  const pairs = [];
  for (const entry of sorted) {
    if (entry.type === "clock_in") {
      open = entry;
    } else if (open) {
      const minutes = Math.max(0, Math.floor((new Date(effectiveTimestamp(entry)) - new Date(effectiveTimestamp(open))) / 60000));
      pairs.push({ in: open, out: entry, minutes });
      open = null;
    }
  }
  return { pairs, open };
}

function entryIncident(entry, label) {
  const pieces = [];
  if (entry?.correction) {
    pieces.push(`${label} corregida de ${formatTime(entry.correction.original_timestamp_utc)} a ${formatTime(entry.correction.corrected_timestamp_utc)} - motivo: ${entry.correction.reason || "-"}`);
  }
  if (entry?.source === "admin") {
    pieces.push(`Fichaje manual${entry.manual_reason ? ` - ${entry.manual_reason}` : ""}`);
  }
  return pieces;
}

function buildPrintGroups(workers, entries, filters) {
  const selectedWorker = workers.find((worker) => worker.id === filters.worker_id);
  const targetWorkers = selectedWorker ? [selectedWorker] : workers.filter((worker) => entries.some((entry) => entry.worker_id === worker.id));
  return targetWorkers.map((worker) => {
    const workerEntries = entries.filter((entry) => entry.worker_id === worker.id);
    const activeEntries = workerEntries.filter((entry) => !entry.voided);
    const { pairs, open } = computePairs(activeEntries);
    const rows = pairs.map((pair) => {
      const origin = pair.in.source === pair.out.source
        ? (pair.in.source === "admin" ? "Admin" : "Trabajador")
        : `${pair.in.source === "admin" ? "Admin" : "Trabajador"} / ${pair.out.source === "admin" ? "Admin" : "Trabajador"}`;
      const incidents = [
        ...entryIncident(pair.in, "Entrada"),
        ...entryIncident(pair.out, "Salida"),
      ];
      return {
        key: `${pair.in.id}-${pair.out.id}`,
        date: formatDate(new Date(effectiveTimestamp(pair.in)).toLocaleDateString("sv-SE", { timeZone: "Europe/Madrid" })),
        clockIn: formatTime(effectiveTimestamp(pair.in)),
        clockOut: formatTime(effectiveTimestamp(pair.out)),
        hours: formatMinutes(pair.minutes),
        minutes: pair.minutes,
        origin,
        incidents: incidents.length ? incidents.join("; ") : "-",
      };
    });
    if (open) {
      rows.push({
        key: open.id,
        date: formatDate(new Date(effectiveTimestamp(open)).toLocaleDateString("sv-SE", { timeZone: "Europe/Madrid" })),
        clockIn: formatTime(effectiveTimestamp(open)),
        clockOut: "PENDIENTE",
        hours: "-",
        minutes: 0,
        origin: open.source === "admin" ? "Admin" : "Trabajador",
        incidents: "Jornada abierta",
      });
    }
    const voided = workerEntries.filter((entry) => entry.voided).map((entry) => ({
      key: entry.id,
      text: `${formatDateTime(effectiveTimestamp(entry))} - ${entry.type === "clock_in" ? "Entrada" : "Salida"} ANULADO - no computa${entry.void_reason ? ` - motivo: ${entry.void_reason}` : ""}`,
    }));
    return {
      worker,
      rows,
      voided,
      totalMinutes: rows.reduce((sum, row) => sum + row.minutes, 0),
    };
  });
}

export default function AdminControlHorario() {
  const [workers, setWorkers] = useState([]);
  const [entries, setEntries] = useState([]);
  const [report, setReport] = useState(null);
  const [settings, setSettings] = useState(null);
  const [requestIp, setRequestIp] = useState(null);
  const [workerDraft, setWorkerDraft] = useState(EMPTY_WORKER);
  const [manualDraft, setManualDraft] = useState({ ...EMPTY_MANUAL, timestamp_local: `${todayISO()}T12:00` });
  const [correctionDraft, setCorrectionDraft] = useState(EMPTY_CORRECTION);
  const [voidDraft, setVoidDraft] = useState(EMPTY_VOID);
  const [deleteTestsText, setDeleteTestsText] = useState("");
  const [deleteTestsOpen, setDeleteTestsOpen] = useState(false);
  const [filters, setFilters] = useState({ worker_id: "", date: todayISO(), date_from: "", date_to: "" });
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const queryParams = useMemo(() => {
    const params = {};
    if (filters.worker_id) params.worker_id = filters.worker_id;
    if (filters.date) params.date = filters.date;
    if (!filters.date && filters.date_from) params.date_from = filters.date_from;
    if (!filters.date && filters.date_to) params.date_to = filters.date_to;
    return params;
  }, [filters]);

  const load = useCallback(async () => {
    setError("");
    try {
      const [workersRes, entriesRes, reportRes, settingsRes, ipRes] = await Promise.all([
        api.get("/admin/workers"),
        api.get("/admin/time-entries", { params: { ...queryParams, include_tests: true } }),
        api.get("/admin/time-report", { params: queryParams }),
        api.get("/admin/time-clock/settings"),
        api.get("/admin/time-clock/request-ip"),
      ]);
      setWorkers(Array.isArray(workersRes.data) ? workersRes.data : []);
      setEntries(Array.isArray(entriesRes.data) ? entriesRes.data : []);
      setReport(reportRes.data || null);
      setSettings(settingsRes.data || null);
      setRequestIp(ipRes.data || null);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    }
  }, [queryParams]);

  useEffect(() => {
    load();
  }, [load]);

  const todaySummary = useMemo(() => {
    const currentModeIsTest = Boolean(settings?.test_mode);
    return workers.map((worker) => {
      const workerEntries = entries.filter((entry) => entry.worker_id === worker.id && isTestEntry(entry) === currentModeIsTest);
      const { pairs, open } = computePairs(workerEntries);
      const minutes = pairs.reduce((sum, pair) => sum + pair.minutes, 0);
      return { worker, open, minutes };
    });
  }, [workers, entries, settings?.test_mode]);

  const officialEntries = useMemo(() => entries.filter((entry) => !isTestEntry(entry)), [entries]);
  const testEntries = useMemo(() => entries.filter((entry) => isTestEntry(entry)), [entries]);

  const printGroups = useMemo(() => buildPrintGroups(workers, officialEntries, filters), [workers, officialEntries, filters]);
  const selectedWorker = useMemo(() => workers.find((worker) => worker.id === filters.worker_id), [workers, filters.worker_id]);
  const generatedAt = new Date().toLocaleString("es-ES", { timeZone: "Europe/Madrid", dateStyle: "short", timeStyle: "short" });
  const periodLabel = useMemo(() => {
    if (filters.date) return { single: true, date: formatDate(filters.date) };
    return {
      single: false,
      from: filters.date_from ? formatDate(filters.date_from) : "Inicio",
      to: filters.date_to ? formatDate(filters.date_to) : "Fin",
    };
  }, [filters]);
  const documentTitle = useMemo(() => {
    const workerName = selectedWorker ? `${selectedWorker.name} ${selectedWorker.surname}` : "Trabajadores";
    const period = filters.date ? formatDate(filters.date).replaceAll("/", "-") : `${periodLabel.from} a ${periodLabel.to}`.replaceAll("/", "-");
    return `Registro horario - ${workerName} - ${period}`;
  }, [filters.date, periodLabel, selectedWorker]);

  const createWorker = async () => {
    setBusy("worker");
    try {
      await api.post("/admin/workers", workerDraft);
      toast.success("Trabajador creado");
      setWorkerDraft(EMPTY_WORKER);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const patchWorker = async (worker, patch) => {
    setBusy(worker.id);
    try {
      await api.patch(`/admin/workers/${worker.id}`, patch);
      toast.success("Trabajador actualizado");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const createManualEntry = async () => {
    setBusy("manual");
    try {
      await api.post("/admin/time-entries/manual", manualDraft);
      toast.success("Fichaje manual creado");
      setManualDraft({ ...EMPTY_MANUAL, timestamp_local: `${todayISO()}T12:00` });
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const correctEntry = async () => {
    setBusy("correction");
    try {
      await api.patch(`/admin/time-entries/${correctionDraft.entry_id}/correction`, {
        corrected_timestamp_local: correctionDraft.corrected_timestamp_local,
        reason: correctionDraft.reason,
      });
      toast.success("Fichaje corregido");
      setCorrectionDraft(EMPTY_CORRECTION);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const voidEntry = async () => {
    setBusy("void");
    try {
      await api.post(`/admin/time-entries/${voidDraft.entry_id}/void`, { reason: voidDraft.reason });
      toast.success("Fichaje anulado");
      setVoidDraft(EMPTY_VOID);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const saveSettings = async (nextSettings) => {
    setBusy("settings");
    try {
      await api.put("/admin/time-clock/settings", nextSettings);
      toast.success("Configuracion guardada");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const toggleTestMode = async (enabled) => {
    const message = enabled
      ? "Los nuevos fichajes quedarán marcados como PRUEBA y no formarán parte del registro horario oficial."
      : "Los nuevos fichajes pasarán a ser registros OFICIALES.";
    if (!confirm(message)) return;
    await saveSettings({
      ...settings,
      test_mode: enabled,
      last_change_reason: enabled ? "Activar modo pruebas" : "Desactivar modo pruebas",
    });
  };

  const deleteTestEntries = async () => {
    setBusy("delete-tests");
    try {
      const { data } = await api.delete("/admin/time-entries/test");
      toast.success(`${data.deleted || 0} registros de prueba eliminados`);
      setDeleteTestsOpen(false);
      setDeleteTestsText("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const handlePrint = () => {
    const previousTitle = document.title;
    document.title = documentTitle;
    window.print();
    window.setTimeout(() => {
      document.title = previousTitle;
    }, 500);
  };

  const csvUrl = `${API}/admin/time-report.csv?${new URLSearchParams(queryParams).toString()}`;

  return (
    <div className="space-y-6" data-testid="admin-control-horario">
      <div className="screen-register space-y-6">
        <header>
          <h1 className="font-display font-black text-3xl">Control horario</h1>
          <p className="text-muted-foreground">Fichajes, horas trabajadas, correcciones y exportacion para Feria.</p>
        </header>

        {error && <div className="rounded-2xl border border-destructive/30 bg-white p-4 font-bold text-destructive">{error}</div>}

        <section className={`rounded-2xl border p-4 ${settings?.test_mode ? "border-amber-300 bg-amber-50" : "border-border bg-white"}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-display font-bold text-xl">Modo de registro</h2>
              {settings?.test_mode ? (
                <div className="mt-1 font-black text-amber-900">MODO PRUEBAS ACTIVO</div>
              ) : (
                <div className="mt-1 font-bold text-emerald-800">REGISTRO OFICIAL</div>
              )}
              {settings?.test_mode && (
                <p className="text-sm font-semibold text-amber-900">Los fichajes realizados ahora no son registros oficiales.</p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => toggleTestMode(!settings?.test_mode)}
                disabled={busy === "settings" || !settings}
                className="rounded-full bg-primary px-4 py-2 text-sm font-bold text-primary-foreground disabled:opacity-60"
              >
                {settings?.test_mode ? "Desactivar modo pruebas" : "Activar modo pruebas"}
              </button>
              {testEntries.length > 0 && (
                <button
                  type="button"
                  onClick={() => setDeleteTestsOpen(true)}
                  className="rounded-full bg-destructive px-4 py-2 text-sm font-bold text-destructive-foreground"
                >
                  Eliminar registros de prueba
                </button>
              )}
            </div>
          </div>
          {deleteTestsOpen && (
            <div className="mt-4 rounded-xl border border-destructive/30 bg-white p-4">
              <p className="font-bold">Vas a eliminar todos los fichajes marcados como PRUEBA.</p>
              <p className="text-sm text-muted-foreground">Los trabajadores y los fichajes oficiales NO se eliminarán.</p>
              <input
                value={deleteTestsText}
                onChange={(e) => setDeleteTestsText(e.target.value)}
                className={`${FIELD_CLASS} mt-3`}
                placeholder={TEST_DELETE_PHRASE}
              />
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={deleteTestEntries}
                  disabled={deleteTestsText !== TEST_DELETE_PHRASE || busy === "delete-tests"}
                  className="rounded-full bg-destructive px-4 py-2 text-sm font-bold text-destructive-foreground disabled:opacity-60"
                >
                  {busy === "delete-tests" ? "Eliminando..." : "Eliminar pruebas definitivamente"}
                </button>
                <button type="button" onClick={() => setDeleteTestsOpen(false)} className="rounded-full bg-accent px-4 py-2 text-sm font-bold">
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </section>

        <section className="rounded-2xl border border-border bg-white p-4">
        <div className="mb-3 flex items-center gap-2">
          <Clock size={18} className="text-primary" />
          <h2 className="font-display font-bold text-xl">Hoy</h2>
        </div>
        <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-3">
          {todaySummary.map(({ worker, open, minutes }) => (
            <article key={worker.id} className="rounded-xl border border-border p-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-bold">{worker.name} {worker.surname}</div>
                  <div className="text-xs uppercase text-muted-foreground">{ROLE_LABELS[worker.role] || worker.role}</div>
                </div>
                <span className={`rounded-full px-2 py-1 text-xs font-black ${open ? "bg-emerald-100 text-emerald-800" : "bg-muted text-muted-foreground"}`}>
                  {open ? "DENTRO" : "FUERA"}
                </span>
              </div>
              <div className="mt-2 text-sm text-muted-foreground">
                {open ? `Dentro desde ${formatTime(effectiveTimestamp(open))}` : "Sin entrada abierta"}
              </div>
              <div className="mt-1 font-display text-lg font-black">{formatMinutes(minutes)} hoy</div>
            </article>
          ))}
        </div>
        </section>

        <section className="grid gap-4 xl:grid-cols-[1.2fr_0.8fr]">
        <div className="rounded-2xl border border-border bg-white p-4">
          <h2 className="font-display font-bold text-xl">Fichajes</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            <select value={filters.worker_id} onChange={(e) => setFilters({ ...filters, worker_id: e.target.value })} className="rounded-lg border border-border px-3 py-2 text-sm">
              <option value="">Todos</option>
              {workers.map((worker) => <option key={worker.id} value={worker.id}>{worker.name} {worker.surname}</option>)}
            </select>
            <input type="date" value={filters.date} onChange={(e) => setFilters({ ...filters, date: e.target.value, date_from: "", date_to: "" })} className="rounded-lg border border-border px-3 py-2 text-sm" />
            <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date: "", date_from: e.target.value })} className="rounded-lg border border-border px-3 py-2 text-sm" />
            <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date: "", date_to: e.target.value })} className="rounded-lg border border-border px-3 py-2 text-sm" />
            <a href={csvUrl} className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-2 text-sm font-bold text-primary-foreground">
              <Download size={14} /> CSV
            </a>
            <button type="button" onClick={handlePrint} className="inline-flex items-center gap-1 rounded-full bg-accent px-3 py-2 text-sm font-bold">
              <Printer size={14} /> Imprimir
            </button>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[780px] text-left text-sm">
              <thead className="border-b border-border text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="py-2">Trabajador</th>
                  <th>Tipo</th>
                  <th>Hora</th>
                  <th>Origen</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id} className="border-b border-border/70">
                    <td className="py-2 font-medium">{entry.worker_name_snapshot}</td>
                    <td>
                      {entry.type === "clock_in" ? "Entrada" : "Salida"}
                      {isTestEntry(entry) && <div className="mt-1 inline-block rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-black text-amber-900">PRUEBA</div>}
                    </td>
                    <td>
                      {formatDateTime(effectiveTimestamp(entry))}
                      {entry.correction && <div className="text-xs text-amber-700">Original: {formatDateTime(entry.timestamp_utc)}</div>}
                    </td>
                    <td>{entry.source === "admin" ? "Admin" : "Trabajador"}</td>
                    <td>
                      {entry.voided ? "Anulado" : entry.correction ? "Corregido" : "OK"}
                      {entry.manual_reason && <div className="text-xs text-muted-foreground">{entry.manual_reason}</div>}
                    </td>
                    <td className="space-x-2">
                      <button type="button" onClick={() => setCorrectionDraft({ entry_id: entry.id, corrected_timestamp_local: toLocalInput(effectiveTimestamp(entry)), reason: "" })} className="text-primary font-bold">Corregir</button>
                      <button type="button" onClick={() => setVoidDraft({ entry_id: entry.id, reason: "" })} className="text-destructive font-bold">Anular</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {report && <div className="mt-4 rounded-xl bg-accent/40 p-3 font-bold">Total periodo: {report.total_hours}</div>}
        </div>

        <div className="space-y-4">
          <Panel title="Fichaje manual" icon={FilePlus2}>
            <select value={manualDraft.worker_id} onChange={(e) => setManualDraft({ ...manualDraft, worker_id: e.target.value })} className={FIELD_CLASS}>
              <option value="">Trabajador</option>
              {workers.map((worker) => <option key={worker.id} value={worker.id}>{worker.name} {worker.surname}</option>)}
            </select>
            <select value={manualDraft.type} onChange={(e) => setManualDraft({ ...manualDraft, type: e.target.value })} className={FIELD_CLASS}>
              <option value="clock_in">Entrada</option>
              <option value="clock_out">Salida</option>
            </select>
            <input type="datetime-local" value={manualDraft.timestamp_local} onChange={(e) => setManualDraft({ ...manualDraft, timestamp_local: e.target.value })} className={FIELD_CLASS} />
            <input value={manualDraft.manual_reason} onChange={(e) => setManualDraft({ ...manualDraft, manual_reason: e.target.value })} className={FIELD_CLASS} placeholder="Motivo obligatorio" />
            <Button onClick={createManualEntry} busy={busy === "manual"}>Añadir fichaje</Button>
          </Panel>

          <Panel title="Correcciones" icon={Save}>
            <input value={correctionDraft.entry_id ? "Fichaje seleccionado" : "Selecciona un fichaje en la tabla"} readOnly className={`${FIELD_CLASS} bg-muted`} />
            <input type="datetime-local" value={correctionDraft.corrected_timestamp_local} onChange={(e) => setCorrectionDraft({ ...correctionDraft, corrected_timestamp_local: e.target.value })} className={FIELD_CLASS} />
            <input value={correctionDraft.reason} onChange={(e) => setCorrectionDraft({ ...correctionDraft, reason: e.target.value })} className={FIELD_CLASS} placeholder="Motivo obligatorio" />
            <Button onClick={correctEntry} busy={busy === "correction"} disabled={!correctionDraft.entry_id}>Guardar corrección</Button>
          </Panel>

          <Panel title="Anular fichaje" icon={Save}>
            <input value={voidDraft.entry_id ? "Fichaje seleccionado" : "Selecciona un fichaje en la tabla"} readOnly className={`${FIELD_CLASS} bg-muted`} />
            <input value={voidDraft.reason} onChange={(e) => setVoidDraft({ ...voidDraft, reason: e.target.value })} className={FIELD_CLASS} placeholder="Motivo obligatorio" />
            <Button onClick={voidEntry} busy={busy === "void"} disabled={!voidDraft.entry_id}>Anular</Button>
          </Panel>
        </div>
        </section>

        <section className="grid gap-4 xl:grid-cols-2">
        <Panel title="Trabajadores" icon={UserPlus}>
          <div className="grid gap-2 md:grid-cols-2">
            <input value={workerDraft.name} onChange={(e) => setWorkerDraft({ ...workerDraft, name: e.target.value })} className={FIELD_CLASS} placeholder="Nombre" />
            <input value={workerDraft.surname} onChange={(e) => setWorkerDraft({ ...workerDraft, surname: e.target.value })} className={FIELD_CLASS} placeholder="Apellidos" />
            <select value={workerDraft.role} onChange={(e) => setWorkerDraft({ ...workerDraft, role: e.target.value })} className={FIELD_CLASS}>
              {ROLES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <input value={workerDraft.pin} onChange={(e) => setWorkerDraft({ ...workerDraft, pin: e.target.value.replace(/\D/g, "").slice(0, 4) })} className={FIELD_CLASS} placeholder="PIN 4 digitos" />
          </div>
          <Button onClick={createWorker} busy={busy === "worker"}>Crear trabajador</Button>
          <div className="mt-4 divide-y divide-border rounded-xl border border-border">
            {workers.map((worker) => (
              <div key={worker.id} className="flex flex-wrap items-center gap-2 p-3 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="font-bold">{worker.name} {worker.surname}</div>
                  <div className="text-xs text-muted-foreground">{ROLE_LABELS[worker.role] || worker.role} · {worker.active ? "Activo" : "Inactivo"}</div>
                </div>
                <button type="button" onClick={() => patchWorker(worker, { active: !worker.active })} className="rounded-full bg-accent px-3 py-1 font-bold">
                  {worker.active ? "Desactivar" : "Activar"}
                </button>
                <button type="button" onClick={() => {
                  const pin = prompt("Nuevo PIN de 4 digitos");
                  if (pin) patchWorker(worker, { pin });
                }} className="rounded-full bg-primary px-3 py-1 font-bold text-primary-foreground">
                  PIN
                </button>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="Red de fichaje" icon={Shield}>
          {settings && (
            <>
              {!settings.network_restriction_enabled && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm font-bold text-amber-800">
                  Restriccion de red desactivada
                </div>
              )}
              <label className="flex items-center gap-2 text-sm font-bold">
                <input
                  type="checkbox"
                  checked={settings.network_restriction_enabled}
                onChange={(e) => setSettings({ ...settings, network_restriction_enabled: e.target.checked })}
                />
                Restringir fichaje a red del stand
              </label>
              <textarea
                value={(settings.authorized_ips || []).join("\n")}
                onChange={(e) => setSettings({ ...settings, authorized_ips: e.target.value.split("\n") })}
                className={`${FIELD_CLASS} min-h-24`}
                placeholder="Una IP o CIDR por linea"
              />
              <input
                value={settings.last_change_reason || ""}
                onChange={(e) => setSettings({ ...settings, last_change_reason: e.target.value })}
                className={FIELD_CLASS}
                placeholder="Motivo del cambio"
              />
              <div className="rounded-xl bg-accent/40 p-3 text-sm">
                <div><span className="font-bold">IP detectada:</span> {requestIp?.resolved_client_ip || "No disponible"}</div>
                <div className="text-xs text-muted-foreground">XFF: {requestIp?.x_forwarded_for || "-"}</div>
                <div className="text-xs text-muted-foreground">X-Real-IP: {requestIp?.x_real_ip || "-"}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => saveSettings(settings)} busy={busy === "settings"}>Guardar red</Button>
                <button
                  type="button"
                  onClick={() => setSettings({ ...settings, authorized_ips: Array.from(new Set([...(settings.authorized_ips || []), requestIp?.resolved_client_ip].filter(Boolean))) })}
                  className="rounded-full bg-accent px-3 py-2 text-sm font-bold"
                >
                  Usar mi IP actual
                </button>
              </div>
            </>
          )}
        </Panel>
        </section>
      </div>

      <PrintRegister
        generatedAt={generatedAt}
        periodLabel={periodLabel}
        selectedWorker={selectedWorker}
        groups={printGroups}
      />
      <style>{`
        .print-register { display: none; }
        @media print {
          @page { size: A4 portrait; margin: 14mm; }
          html, body, #root {
            height: auto !important;
            min-height: 0 !important;
            overflow: visible !important;
            background: white !important;
          }
          body { color: #111 !important; }
          .screen-register,
          .screen-register *,
          .print-register ~ * {
            display: none !important;
          }
          .admin-no-print {
            display: none !important;
          }
          .admin-layout,
          .admin-shell,
          .admin-main {
            display: block !important;
            width: auto !important;
            max-width: none !important;
            min-width: 0 !important;
            height: auto !important;
            min-height: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: visible !important;
            background: white !important;
          }
          .print-register {
            display: block !important;
            position: static !important;
            width: 100%;
            margin: 0 !important;
            padding: 0 !important;
            background: white;
            color: #111;
            font-family: Arial, sans-serif;
            font-size: 11px;
            line-height: 1.35;
            overflow: visible !important;
          }
          .print-register table { width: 100%; border-collapse: collapse; page-break-inside: auto; }
          .print-register tr { page-break-inside: avoid; page-break-after: auto; }
          .print-register th, .print-register td { border: 1px solid #333; padding: 5px 6px; vertical-align: top; }
          .print-register th { background: #f1f1f1 !important; font-weight: 700; text-align: left; }
          .print-worker { page-break-inside: avoid; margin-top: 18px; }
          .print-worker + .print-worker { page-break-before: auto; }
          .print-muted { color: #444; }
          .print-total { margin-top: 8px; font-weight: 700; text-align: right; }
          .print-incidents { margin-top: 8px; border: 1px solid #333; padding: 6px; }
        }
      `}</style>
    </div>
  );
}

function PrintRegister({ generatedAt, periodLabel, selectedWorker, groups }) {
  return (
    <section className="print-register">
      <header>
        <div style={{ fontWeight: 700, fontSize: 15 }}>Casa del Pueblo</div>
        <div className="print-muted">Feria de Albacete</div>
        <h1 style={{ margin: "14px 0 4px", fontSize: 20, letterSpacing: 0 }}>REGISTRO DE JORNADA</h1>
        <div>Fecha de generación: {generatedAt}</div>
      </header>

      <section style={{ marginTop: 14 }}>
        <h2 style={{ margin: "0 0 4px", fontSize: 13 }}>Periodo</h2>
        {periodLabel.single ? (
          <div>Fecha: {periodLabel.date}</div>
        ) : (
          <div>Desde: {periodLabel.from} · Hasta: {periodLabel.to}</div>
        )}
      </section>

      <section style={{ marginTop: 14 }}>
        {selectedWorker ? (
          <>
            <h2 style={{ margin: "0 0 4px", fontSize: 13 }}>Trabajador</h2>
            <div>{selectedWorker.name} {selectedWorker.surname}</div>
            <div className="print-muted">Rol: {ROLE_LABELS[selectedWorker.role] || selectedWorker.role}</div>
          </>
        ) : (
          <h2 style={{ margin: 0, fontSize: 13 }}>Registro horario de trabajadores</h2>
        )}
      </section>

      {groups.length === 0 ? (
        <div style={{ marginTop: 18 }}>No hay fichajes para los filtros seleccionados.</div>
      ) : (
        groups.map((group) => (
          <section key={group.worker.id} className="print-worker">
            {!selectedWorker && (
              <h2 style={{ margin: "0 0 6px", fontSize: 14 }}>
                {group.worker.name} {group.worker.surname}
                <span className="print-muted" style={{ fontWeight: 400 }}> · {ROLE_LABELS[group.worker.role] || group.worker.role}</span>
              </h2>
            )}
            <table>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Entrada</th>
                  <th>Salida</th>
                  <th>Horas</th>
                  <th>Origen</th>
                  <th>Incidencias / correcciones</th>
                </tr>
              </thead>
              <tbody>
                {group.rows.length === 0 ? (
                  <tr>
                    <td colSpan="6">Sin jornadas computables.</td>
                  </tr>
                ) : (
                  group.rows.map((row) => (
                    <tr key={row.key}>
                      <td>{row.date}</td>
                      <td>{row.clockIn}</td>
                      <td>{row.clockOut}</td>
                      <td>{row.hours}</td>
                      <td>{row.origin}</td>
                      <td>{row.incidents}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {group.voided.length > 0 && (
              <div className="print-incidents">
                <strong>Fichajes anulados</strong>
                {group.voided.map((item) => (
                  <div key={item.key}>{item.text}</div>
                ))}
              </div>
            )}
            <div className="print-total">TOTAL HORAS DEL PERIODO: {formatMinutes(group.totalMinutes)}</div>
          </section>
        ))
      )}
    </section>
  );
}

function Panel({ title, icon: Icon, children }) {
  return (
    <section className="rounded-2xl border border-border bg-white p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Icon size={17} className="text-primary" />
        <h2 className="font-display font-bold text-xl">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function Button({ children, onClick, busy, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy || disabled}
      className="inline-flex items-center justify-center rounded-full bg-primary px-4 py-2 text-sm font-bold text-primary-foreground disabled:opacity-60"
    >
      {busy ? "Guardando..." : children}
    </button>
  );
}
